import {Sparkles, BrainCircuit, Target, Users, Clock, Zap, Award, MessageSquare, Quote, Copy, CheckCircle, TrendingUp, MousePointer2, Timer, Scale, Layers} from "lucide-react"
import { formatText } from "../../DanvinciHelpers";

export const SynopsisTab = ({danteSummary, access, fitScore, caption, recommendedMedium, timingRationale, strategicRationale, phase, audience, rank}) => {
    
    // --- DATA MINING ---
    // 1. Lift Metrics
    const ctrLift = access('predictive_performance_metrics.projected_metrics.ctr_lift') || "N/A";
    const convLift = access('predictive_performance_metrics.projected_metrics.conversion_lift') || "N/A";
    const performanceTier = access('predictive_performance_metrics.scientific_uplift_modeling.performance_tier') || "STANDARD";
    
    // 2. Dwell Time
    const dwellTime = access('attention_capture_analysis.estimated_dwell_time_seconds') || 0;

    // 3. NEW: Message Consistency (The "Better Data Point")
    // This replaces the scientific basis with specific campaign alignment data
    const consistency = access('messageConsistencyAnalysis') || {};
    const consistencyScore = (consistency.overall_consistency_score || 0) * 100;
    const consistencyLevel = consistency.consistency_assessment || "UNKNOWN";
    const themes = consistency.dominant_themes || [];

    // 4. Psychological Vibe
    const psychVibe = access('visual_rhetoric_analysis.visualBrandAnalysis.psychological_vibe');

    // Tooltip Component (Updated: 1s delay enter, instant exit)
    const Tooltip = ({ text }) => (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-56 p-3 bg-slate-950/95 border border-slate-700 text-slate-300 text-[10px] leading-relaxed rounded-lg shadow-xl opacity-0 transition-opacity duration-0 delay-0 group-hover:opacity-100 group-hover:duration-300 group-hover:delay-1000 pointer-events-none z-50">
            {text}
            <div className="absolute bottom-[-4px] left-1/2 -translate-x-1/2 w-2 h-2 bg-slate-950 border-r border-b border-slate-700 rotate-45"></div>
        </div>
    );

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500 pb-10">
            
            {/* SECTION 1: DANTE'S STRATEGIC VERDICT */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Left: The AI Synthesis */}
                <div className="lg:col-span-2 bg-gradient-to-br from-slate-800 to-slate-900 p-5 rounded-xl border border-indigo-500/30 shadow-lg relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-3 opacity-10">
                        <Sparkles size={80} className="text-indigo-400" />
                    </div>
                    <div className="relative z-10">
                        <h3 className="text-xs font-bold text-indigo-300 uppercase tracking-widest mb-3 flex items-center gap-2">
                            <BrainCircuit size={14} className="text-indigo-400" /> Dante Strategic Synthesis
                        </h3>
                        <p className="text-sm text-slate-200 leading-relaxed font-medium">
                            "{danteSummary}"
                        </p>
                        
                        {/* Context Pills */}
                        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-indigo-500/20">
                             {access('composition_analysis.contextual_theme') && (
                                <span className="px-2 py-1 bg-indigo-950/50 border border-indigo-500/30 text-indigo-300 text-[10px] uppercase font-bold rounded flex items-center gap-1.5">
                                    <Target size={10} /> {access('composition_analysis.contextual_theme')}
                                </span>
                            )}
                            {psychVibe && (
                                <span className="px-2 py-1 bg-fuchsia-950/50 border border-fuchsia-500/30 text-fuchsia-300 text-[10px] uppercase font-bold rounded flex items-center gap-1.5">
                                    <Sparkles size={10} /> {psychVibe}
                                </span>
                            )}
                            <span className="px-2 py-1 bg-slate-800 border border-slate-600 text-slate-400 text-[10px] uppercase font-bold rounded flex items-center gap-1.5">
                                <Users size={10} /> {audience}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Right: The Scorecard */}
                <div className={`p-5 rounded-xl border-2 flex flex-col justify-between relative overflow-hidden ${
                    fitScore > 80 ? 'bg-emerald-950/30 border-emerald-500/50' :
                    fitScore > 60 ? 'bg-cyan-950/30 border-cyan-500/50' :
                    'bg-amber-950/30 border-amber-500/50'
                }`}>
                    <div>
                        <div className="flex justify-between items-start mb-2">
                            <span className="text-[10px] uppercase tracking-widest font-bold opacity-70 text-white">Strategic Fit</span>
                            <span className="bg-black/40 backdrop-blur-md px-2 py-1 rounded text-xs font-bold text-white border border-white/10">#{rank}</span>
                        </div>
                        <div className={`text-5xl font-black font-mono tracking-tighter mb-1 ${
                            fitScore > 80 ? 'text-emerald-400' :
                            fitScore > 60 ? 'text-cyan-400' :
                            'text-amber-400'
                        }`}>
                            {fitScore.toFixed(0)}%
                        </div>
                        <div className="text-xs text-slate-300 font-medium opacity-80">
                            Match Probability
                        </div>
                    </div>

                    {/* Viral / Authenticity Badges */}
                    <div className="flex gap-2 mt-4">
                       {access('viral_potential_analysis.shareability_category') === 'HIGH' ? (
                            <div className="flex-1 bg-purple-500/20 border border-purple-500/40 rounded py-1.5 flex flex-col items-center justify-center text-center">
                                <Zap size={12} className="text-purple-400 mb-0.5" />
                                <span className="text-[9px] font-bold text-purple-300 uppercase">Viral</span>
                            </div>
                       ) : (
                            <div className="flex-1 bg-slate-800/40 border border-slate-700/50 rounded py-1.5 flex flex-col items-center justify-center text-center opacity-60">
                                <Zap size={12} className="text-slate-500 mb-0.5" />
                                <span className="text-[9px] font-bold text-slate-500 uppercase">Low Viral</span>
                            </div>
                       )}
                       
                       {access('authenticity_differentiation.category') === 'HIGHLY_AUTHENTIC' ? (
                            <div className="flex-1 bg-teal-500/20 border border-teal-500/40 rounded py-1.5 flex flex-col items-center justify-center text-center">
                                <Award size={12} className="text-teal-400 mb-0.5" />
                                <span className="text-[9px] font-bold text-teal-300 uppercase">Authentic</span>
                            </div>
                       ) : (
                            <div className="flex-1 bg-slate-800/40 border border-slate-700/50 rounded py-1.5 flex flex-col items-center justify-center text-center opacity-60">
                                <Award size={12} className="text-slate-500 mb-0.5" />
                                <span className="text-[9px] font-bold text-slate-500 uppercase">Generic</span>
                            </div>
                       )}
                    </div>
                </div>
            </div>

            {/* SECTION 2: EXECUTION & COPY */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Suggested Caption */}
                <div className="bg-slate-800/40 rounded-xl border border-slate-700/50 p-5 flex flex-col">
                    <div className="flex justify-between items-center mb-3">
                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
                            <MessageSquare size={14} className="text-cyan-400"/> Suggested Caption
                        </h4>
                        <button 
                            className="text-xs flex items-center gap-1.5 px-2 py-1 bg-slate-700/50 hover:bg-slate-700 text-slate-300 rounded transition-colors"
                            onClick={() => caption && navigator.clipboard.writeText(caption)}
                        >
                            <Copy size={12} /> <span className="font-bold">Copy</span>
                        </button>
                    </div>
                    <div className="flex-1 bg-slate-900/50 rounded-lg p-4 border border-black/20 relative group">
                        <Quote size={16} className="absolute top-3 left-3 text-slate-700" />
                        <p className="text-sm text-slate-200 italic font-medium leading-relaxed pl-6">
                            "{caption || "No caption generated."}"
                        </p>
                    </div>
                </div>

                {/* Recommended Medium */}
                <div className="bg-slate-800/40 rounded-xl border border-slate-700/50 p-5 flex flex-col">
                     <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2 mb-3">
                        <CheckCircle size={14} className="text-emerald-400"/> Execution Strategy
                    </h4>
                    
                    <div className="flex-1 flex flex-col justify-center bg-gradient-to-r from-slate-900/80 to-slate-800/80 rounded-lg p-4 border border-slate-700/30">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-slate-500 uppercase font-bold">Optimal Channel</span>
                            <span className="text-xs font-mono text-emerald-400 font-bold">
                                {fitScore.toFixed(0)}% Match
                            </span>
                        </div>
                        <div className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                            {recommendedMedium}
                        </div>
                        <p className="text-xs text-slate-400 leading-relaxed border-t border-white/5 pt-2 mt-auto">
                            {strategicRationale || timingRationale || "Deploy during high-engagement windows."}
                        </p>
                    </div>
                </div>
            </div>

            {/* SECTION 3: PREDICTIVE IMPACT & CONSISTENCY */}
            <div>
                 <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3 pl-1">Predictive Impact</h4>
                 <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    
                    {/* The Metrics Bar */}
                    <div className="lg:col-span-2 bg-slate-900/40 rounded-xl border border-slate-700/30 p-1">
                        <div className="grid grid-cols-3 divide-x divide-slate-800">
                            
                            {/* 1. CTR Uplift */}
                            <div className="p-4 flex flex-col items-center text-center group relative cursor-help">
                                <span className="text-[10px] uppercase font-bold text-slate-500 mb-1 flex items-center gap-1">
                                    <MousePointer2 size={10} /> CTR Lift
                                </span>
                                <span className="text-2xl font-black text-white font-mono">{ctrLift}</span>
                                <span className="text-[10px] text-emerald-400 font-bold bg-emerald-950/30 px-1.5 py-0.5 rounded mt-1">
                                    Projected
                                </span>
                                <Tooltip text="The estimated percentage increase in Click-Through Rate compared to the sector average for this content type." />
                            </div>

                            {/* 2. Conversion Uplift */}
                            <div className="p-4 flex flex-col items-center text-center group relative cursor-help">
                                <span className="text-[10px] uppercase font-bold text-slate-500 mb-1 flex items-center gap-1">
                                    <TrendingUp size={10} /> Conv. Lift
                                </span>
                                <span className="text-2xl font-black text-emerald-400 font-mono">{convLift}</span>
                                <span className="text-[10px] text-emerald-500/70 mt-1">
                                    vs. Baseline
                                </span>
                                <Tooltip text="The projected lift in downstream conversion probability, driven by high emotional resonance and trust markers." />
                            </div>

                            {/* 3. Performance Tier */}
                            <div className="p-4 flex flex-col items-center text-center group relative cursor-help">
                                <span className="text-[10px] uppercase font-bold text-slate-500 mb-1 flex items-center gap-1">
                                    <Award size={10} /> Tier
                                </span>
                                <span className={`text-lg font-black font-mono mt-0.5 ${
                                    performanceTier === 'HIGH' || performanceTier === 'EXCELLENT' ? 'text-purple-400' : 'text-slate-300'
                                }`}>
                                    {performanceTier}
                                </span>
                                <span className="text-[10px] text-slate-500 mt-1">
                                    Forecast Class
                                </span>
                                <Tooltip text="AI classification of this asset's potential relative to the campaign's historical performance distribution." />
                            </div>
                        </div>
                    </div>

                    {/* NEW: Message Consistency Analysis (Replaces Scientific Basis) */}
                    <div className="bg-slate-800/30 rounded-xl border border-slate-700/30 p-4 flex flex-col justify-center">
                        <div className="flex justify-between items-center mb-2">
                            <div className="flex items-center gap-2">
                                <Scale size={12} className="text-blue-400"/>
                                <span className="text-[10px] uppercase font-bold text-blue-300">Campaign Fit</span>
                            </div>
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                                consistencyScore > 70 ? 'bg-emerald-950 text-emerald-400' : 
                                consistencyScore > 50 ? 'bg-blue-950 text-blue-400' : 'bg-red-950 text-red-400'
                            }`}>
                                {consistencyScore.toFixed(0)}%
                            </span>
                        </div>
                        
                        <div className="text-xs font-bold text-white mb-2 capitalize">
                            {formatText(consistencyLevel)} Consistency
                        </div>
                        
                        {themes.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5">
                                {themes.slice(0, 3).map((theme, i) => (
                                    <span key={i} className="text-[9px] px-1.5 py-0.5 bg-slate-700/50 border border-slate-600 rounded text-slate-300 capitalize">
                                        {theme}
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <p className="text-[10px] text-slate-500 italic">No dominant themes detected.</p>
                        )}
                    </div>
                 </div>
            </div>

            {/* SECTION 4: PRIMARY METRICS */}
            <div>
                <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3 pl-1">Primary Metrics</h4>
                <div className="grid grid-cols-3 gap-4">
                    
                    {/* Metric 1: Aesthetic */}
                    <div className="bg-slate-900/40 p-3 rounded-lg border border-slate-700/30 flex items-center gap-4">
                        <div className="relative w-12 h-12 flex-shrink-0">
                             <svg className="w-full h-full transform -rotate-90">
                                <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="4" fill="transparent" className="text-slate-800" />
                                <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="4" fill="transparent" 
                                    strokeDasharray={2 * Math.PI * 20} 
                                    strokeDashoffset={2 * Math.PI * 20 * (1 - (access('aesthetic_quality_score') || 0))} 
                                    className="text-fuchsia-400 transition-all duration-1000" 
                                />
                            </svg>
                            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white">
                                {((access('aesthetic_quality_score') || 0) * 100).toFixed(0)}
                            </span>
                        </div>
                        <div>
                            <div className="text-xs font-bold text-white">Aesthetic</div>
                            <div className="text-[10px] text-slate-500">Visual Polish</div>
                        </div>
                    </div>

                    {/* Metric 2: Attention (Numerical Only) */}
                    <div className="bg-slate-900/40 p-3 rounded-lg border border-slate-700/30 flex items-center justify-between px-5 group relative">
                        <div className="flex flex-col">
                            <span className="text-[10px] text-slate-500 uppercase font-bold flex items-center gap-1.5">
                                <Timer size={12} className="text-amber-400" /> Est. Dwell
                            </span>
                            <span className="text-2xl font-black text-white font-mono mt-1">
                                {Number(dwellTime).toFixed(1)}s
                            </span>
                        </div>
                        <div className="h-8 w-px bg-slate-700/50 mx-2"></div>
                        <div className="text-right">
                             <div className="text-[10px] text-slate-500 uppercase font-bold">Capture</div>
                             <div className="text-lg font-bold text-amber-400">
                                {((access('attention_capture_analysis.attention_capture_score') || 0) * 100).toFixed(0)}%
                             </div>
                        </div>

                        {/* Hover Tooltip for Drivers */}
                        {access('attention_capture_analysis.attention_drivers')?.length > 0 && (
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 bg-black/90 border border-slate-700 rounded-lg p-3 opacity-0 transition-opacity duration-0 delay-0 group-hover:opacity-100 group-hover:duration-300 group-hover:delay-1000 pointer-events-none z-50 shadow-xl">
                                <div className="text-[9px] uppercase text-slate-500 font-bold mb-1">Drivers</div>
                                <ul className="space-y-1">
                                    {access('attention_capture_analysis.attention_drivers').map((d, i) => (
                                        <li key={i} className="text-[10px] text-slate-300">• {d}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>

                    {/* Metric 3: Authenticity */}
                    <div className="bg-slate-900/40 p-3 rounded-lg border border-slate-700/30 flex items-center gap-4">
                        <div className="relative w-12 h-12 flex-shrink-0">
                             <svg className="w-full h-full transform -rotate-90">
                                <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="4" fill="transparent" className="text-slate-800" />
                                <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="4" fill="transparent" 
                                    strokeDasharray={2 * Math.PI * 20} 
                                    strokeDashoffset={2 * Math.PI * 20 * (1 - (access('authenticity_differentiation.authenticity_score') || 0))} 
                                    className="text-teal-400 transition-all duration-1000" 
                                />
                            </svg>
                            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white">
                                {((access('authenticity_differentiation.authenticity_score') || 0) * 100).toFixed(0)}
                            </span>
                        </div>
                        <div>
                            <div className="text-xs font-bold text-white">Authenticity</div>
                            <div className="text-[10px] text-slate-500">Unique Signal</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};